import {
  Badge,
  Banner,
  Button,
  Dialog,
  Empty,
  Loader,
  Select,
  Surface,
  Table,
  Text,
} from '@cloudflare/kumo'
import { ArrowsClockwise, ChatCircle, Flask, Prohibit, Star, Trash, X } from '@phosphor-icons/react'
import {
  CONCERN_TAGS,
  isExamplePost,
  LANGUAGE_NAMES,
  LANGUAGES,
  productCategoryLabel,
  taxonomyLabel,
  type Language,
} from '@repo/shared'
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router'
import { adminApi, type AdminComment, type AdminPost, type Page } from '../api'
import { casesApi, type PostDeleteResult } from '../cases-api'
import { ConfirmDialog } from '../components/confirm-dialog'
import { HelpPanel } from '../components/help-panel'
import { MenuTagList } from '../components/menu-tag-list'
import { PageHeader } from '../components/page-header'
import { useGetToken } from '../lib/auth'
import {
  concernLabels,
  costShareOf,
  filterPosts,
  hasPostFilter,
  loadAllPages,
  menuTagRows,
  NO_POST_FILTER,
  type AllPages,
  type PostFilter,
  type PostSourceFilter,
} from '../lib/community-concerns'
import { fmtInt, fmtMoney, fmtTime, toMessage } from '../lib/format'
import { TablePagination } from '../lib/table-pagination'
import { usePage, usePagedList } from '../lib/use-page'

const LANG_ITEMS = LANGUAGES.map((l) => ({ label: `${l} · ${LANGUAGE_NAMES[l]}`, value: l }))

const ALL = 'all'
const SOURCE_ITEMS: { label: string; value: PostSourceFilter }[] = [
  { label: '글: 전체', value: 'all' },
  { label: '예시 글만', value: 'example' },
  { label: '사용자 글만', value: 'user' },
]
const CONCERN_ITEMS = [
  { label: '고민 유형: 전체', value: ALL },
  ...CONCERN_TAGS.map((t) => ({ label: `고민 유형: ${t.ko}`, value: t.id })),
]

/** 이용 제한·해제 확인 대상 (글 작성자 또는 댓글 작성자) */
interface RestrictTarget {
  userId: string
  name: string
  restrict: boolean
}

/**
 * 커뮤니티 글 — 목록 + 홈 추천 토글 + 언어 재지정 + 삭제(soft) + 신고 처리.
 * 추천(★)은 앱 홈 상단에 같은 언어 사용자에게 최근 추천순 3개까지 보인다.
 * 신고 처리 (App Store 1.2 — 약관의 "24시간 안에 검토"): 신고 건수를 보고 글·댓글을 지우고, 작성자의 커뮤니티 이용을 제한한다.
 * 예시 글 표시·고민 유형·메뉴 꼬리표·원가 공개 (2026-10-23): 서버 목록은 페이지만 받으므로 거르기를 켜면
 * 글을 끝까지 받아 여기서 거르고 10개씩 나눈다.
 */
export function CommunityPage() {
  const getToken = useGetToken()
  const { query, setPage } = usePage()
  const [data, setData] = useState<Page<AdminPost> | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [filter, setFilter] = useState<PostFilter>(NO_POST_FILTER)
  const filtering = hasPostFilter(filter)
  const [scan, setScan] = useState<AllPages<AdminPost> | null>(null)
  const filtered = useMemo(() => (scan ? filterPosts(scan.items, filter) : null), [scan, filter])
  const paged = usePagedList(filtered)
  const items = filtering ? (filtered ? paged.slice : null) : (data?.items ?? null)

  const reload = useCallback(async () => {
    setBusy(true)
    try {
      if (filtering) setScan(await loadAllPages((q) => adminApi.community.list(getToken, q)))
      else setData(await adminApi.community.list(getToken, query))
      setError(null)
    } catch (err) {
      setError(toMessage(err))
    } finally {
      setBusy(false)
    }
  }, [getToken, query, filtering])

  const changeFilter = (patch: Partial<PostFilter>) => {
    setFilter((f) => ({ ...f, ...patch }))
    paged.setPage(1)
  }

  const [detail, setDetail] = useState<AdminPost | null>(null)

  useEffect(() => {
    void reload()
  }, [reload])

  const patch = async (post: AdminPost, body: { featured?: boolean; language?: Language }) => {
    try {
      await adminApi.community.patch(getToken, post.id, body)
      await reload()
    } catch (err) {
      setError(toMessage(err))
    }
  }

  const [deleting, setDeleting] = useState<AdminPost | null>(null)
  const remove = async () => {
    if (!deleting) return
    setBusy(true)
    try {
      const res: PostDeleteResult = await adminApi.community.remove(getToken, deleting.id)
      setDeleting(null)
      if (res.linkedCaseIds?.length) setLinkedCaseIds(res.linkedCaseIds)
      await reload()
    } catch (err) {
      setError(toMessage(err))
    } finally {
      setBusy(false)
    }
  }

  // 지운 글의 완성 사례는 갤러리에 남는다. 함께 숨길지 묻는다 (C19)
  const [linkedCaseIds, setLinkedCaseIds] = useState<string[] | null>(null)
  const hideLinkedCases = async () => {
    if (!linkedCaseIds) return
    setBusy(true)
    try {
      await casesApi.hideAll(getToken, linkedCaseIds)
      setLinkedCaseIds(null)
    } catch (err) {
      setError(toMessage(err))
    } finally {
      setBusy(false)
    }
  }

  // ── 댓글 보기 · 삭제 ──
  const [commentsOf, setCommentsOf] = useState<AdminPost | null>(null)
  const [comments, setComments] = useState<AdminComment[] | null>(null)
  const [commentsError, setCommentsError] = useState<string | null>(null)
  const loadComments = useCallback(
    async (post: AdminPost) => {
      try {
        setComments((await adminApi.community.comments(getToken, post.id)).items)
        setCommentsError(null)
      } catch (err) {
        setCommentsError(toMessage(err))
      }
    },
    [getToken],
  )
  const openComments = (post: AdminPost) => {
    setCommentsOf(post)
    setComments(null)
    setCommentsError(null)
    void loadComments(post)
  }

  const [deletingComment, setDeletingComment] = useState<AdminComment | null>(null)
  const removeComment = async () => {
    if (!deletingComment) return
    setBusy(true)
    try {
      await adminApi.community.removeComment(getToken, deletingComment.id)
      setDeletingComment(null)
      if (commentsOf) await loadComments(commentsOf)
      await reload()
    } catch (err) {
      setCommentsError(toMessage(err))
      setDeletingComment(null)
    } finally {
      setBusy(false)
    }
  }

  // ── 작성자 이용 제한 · 해제 ──
  const [restricting, setRestricting] = useState<RestrictTarget | null>(null)
  const applyRestrict = async () => {
    if (!restricting) return
    setBusy(true)
    try {
      await adminApi.community.restrict(getToken, restricting.userId, restricting.restrict)
      setRestricting(null)
      if (commentsOf) await loadComments(commentsOf)
      await reload()
    } catch (err) {
      setError(toMessage(err))
      setRestricting(null)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="커뮤니티"
        description="사용자가 올린 글과 투표예요. 홈에 띄울 글을 추천하고, 문제가 되는 글은 숨겨요."
        actions={
          <Button
            variant="secondary"
            icon={<ArrowsClockwise />}
            loading={busy}
            onClick={() => void reload()}
          >
            새로고침
          </Button>
        }
      />

      <HelpPanel
        id="community"
        steps={[
          <>
            <b>추천(★)</b> 을 누르면 그 글이 앱 홈 상단에 떠요 — 같은 언어 사용자에게, 최근
            추천순으로 3개까지.
          </>,
          <>
            기기 언어와 다른 언어로 쓴 글은 <b>언어</b> 를 바로잡아 주세요. 그래야 맞는 사용자에게
            보여요.
          </>,
          <>
            스팸·부적절한 글은 <b>삭제</b>하세요. 모든 사용자에게서 숨겨지고 되돌릴 수 없어요.
          </>,
          <>
            <b>신고</b> 가 붙은 글은 24시간 안에 확인하세요. <b>댓글</b> 수를 누르면 댓글을 보고
            지울 수 있어요. 규칙을 어긴 작성자는 <b>이용 제한</b> 하면 글·댓글을 더 쓸 수 없어요
            (해제 가능).
          </>,
          <>
            제목을 누르면 <b>글 상세</b>가 열려요. 고민 유형, 메뉴 꼬리표, 붙인 제품의 원가·마진
            공개 여부를 볼 수 있어요.
          </>,
          <>
            <b>예시 고민</b> 표시는 운영팀이 넣은 예시 글이에요. 위 거르기로 예시 글만, 고민
            유형별로 볼 수 있어요.
          </>,
        ]}
        notes={[
          '고민 유형 거르기는 앱 칩과 같은 기준이에요. 국제·서양 메인, 기법 중심, 식이 제한, 테스트 중은 글이 고르지 않았어도 붙인 제품 분류나 메뉴 꼬리표로 걸려요.',
          '원가·마진 공개는 글쓴이가 켠 경우만이에요. 켜면 앱의 제품 카드에 원가와 마진이 모두에게 보여요.',
        ]}
      />

      {error && <Banner variant="error" title="문제가 생겼어요" description={error} />}

      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-end gap-2">
          <Select
            aria-label="글 출처"
            size="sm"
            className="min-w-36"
            value={filter.source}
            onValueChange={(v: string | null) =>
              changeFilter({ source: (v ?? 'all') as PostSourceFilter })
            }
            items={SOURCE_ITEMS}
          />
          <Select
            aria-label="고민 유형"
            size="sm"
            className="min-w-56"
            value={filter.concern ?? ALL}
            onValueChange={(v: string | null) =>
              changeFilter({ concern: !v || v === ALL ? null : v })
            }
            items={CONCERN_ITEMS}
          />
          {filtering && (
            <Button
              variant="ghost"
              size="sm"
              icon={<X />}
              onClick={() => changeFilter(NO_POST_FILTER)}
            >
              필터 지우기
            </Button>
          )}
        </div>
        {filtering && scan && filtered && (
          <Text variant="secondary" size="xs">
            {scan.truncated
              ? `최근 글 ${fmtInt(scan.items.length)}건 안에서 ${fmtInt(filtered.length)}건이 맞아요. 전체는 ${fmtInt(scan.total)}건이에요.`
              : `전체 ${fmtInt(scan.total)}건 중 ${fmtInt(filtered.length)}건이 맞아요.`}
          </Text>
        )}
      </div>

      <Surface className="overflow-x-auto rounded-lg p-0">
        {items === null ? (
          <div className="flex items-center gap-3 p-6">
            <Loader />
            <Text variant="secondary">불러오는 중…</Text>
          </div>
        ) : items.length === 0 ? (
          filtering ? (
            <Empty title="조건에 맞는 글이 없어요" description="거르기를 바꾸거나 지워 보세요." />
          ) : (
            <Empty title="아직 글이 없어요" description="사용자가 글을 올리면 여기에 나타나요." />
          )
        ) : (
          <Table>
            <Table.Header variant="compact">
              <Table.Row>
                <Table.Head>작성</Table.Head>
                <Table.Head>작성자</Table.Head>
                <Table.Head>제목</Table.Head>
                <Table.Head>투표 보기</Table.Head>
                <Table.Head>카테고리</Table.Head>
                <Table.Head>고민 유형</Table.Head>
                <Table.Head>언어</Table.Head>
                <Table.Head>홈 추천</Table.Head>
                <Table.Head>투표</Table.Head>
                <Table.Head>댓글</Table.Head>
                <Table.Head>신고</Table.Head>
                <Table.Head>상태</Table.Head>
                <Table.Head>
                  <span className="sr-only">작업</span>
                </Table.Head>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {items.map((p) => (
                <Table.Row key={p.id}>
                  <Table.Cell className="whitespace-nowrap">{fmtTime(p.createdAt)}</Table.Cell>
                  <Table.Cell>
                    <div className="flex flex-col items-start gap-1">
                      <span>{p.authorName}</span>
                      {isExamplePost(p) && <Badge variant="info">예시 고민</Badge>}
                      {p.authorRestricted && <Badge variant="error">이용 제한</Badge>}
                    </div>
                  </Table.Cell>
                  <Table.Cell className="max-w-xs">
                    <button
                      type="button"
                      className="text-kumo-link block max-w-full truncate text-left hover:underline"
                      onClick={() => setDetail(p)}
                    >
                      {p.question}
                    </button>
                    <div className="text-kumo-subtle truncate text-xs">
                      {[
                        p.product?.name,
                        p.body,
                        p.images.length ? `사진 ${p.images.length}장` : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                    {costShareOf(p.product) === 'shared' && (
                      <Badge variant="warning">원가·마진 공개</Badge>
                    )}
                  </Table.Cell>
                  <Table.Cell className="max-w-xs">
                    <div className="truncate">{p.options.length ? p.options.join(' / ') : '—'}</div>
                  </Table.Cell>
                  <Table.Cell>
                    {p.categoryId ? (
                      <Badge variant="outline">{productCategoryLabel(p.categoryId)}</Badge>
                    ) : (
                      '—'
                    )}
                  </Table.Cell>
                  <Table.Cell className="min-w-40 max-w-56">
                    <TagSummary post={p} />
                  </Table.Cell>
                  <Table.Cell>
                    <Select
                      aria-label="언어"
                      size="sm"
                      className="min-w-32"
                      value={p.language as Language}
                      onValueChange={(v: string | null) => {
                        if (v && v !== p.language) void patch(p, { language: v as Language })
                      }}
                      items={LANG_ITEMS}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    {p.isDeleted ? (
                      '—'
                    ) : (
                      <Button
                        variant={p.featuredAt ? 'primary' : 'secondary'}
                        size="sm"
                        icon={<Star weight={p.featuredAt ? 'fill' : 'regular'} />}
                        onClick={() => void patch(p, { featured: !p.featuredAt })}
                      >
                        {p.featuredAt ? '추천 중' : '추천'}
                      </Button>
                    )}
                  </Table.Cell>
                  <Table.Cell>{p.options.length ? p.totalVotes : '—'}</Table.Cell>
                  <Table.Cell>
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={<ChatCircle />}
                      aria-label="댓글 보기"
                      onClick={() => openComments(p)}
                    >
                      {p.commentCount}
                    </Button>
                  </Table.Cell>
                  <Table.Cell>
                    {p.reportCount > 0 ? <Badge variant="warning">{p.reportCount}건</Badge> : '—'}
                  </Table.Cell>
                  <Table.Cell>
                    {p.isDeleted ? (
                      <Badge variant="error">삭제됨</Badge>
                    ) : (
                      <Badge variant="success">게시 중</Badge>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex gap-2">
                      <Button
                        variant="secondary"
                        shape="square"
                        icon={<Prohibit />}
                        aria-label={
                          p.authorRestricted ? '작성자 이용 제한 해제' : '작성자 이용 제한'
                        }
                        onClick={() =>
                          setRestricting({
                            userId: p.userId,
                            name: p.authorName,
                            restrict: !p.authorRestricted,
                          })
                        }
                      />
                      {!p.isDeleted && (
                        <Button
                          variant="secondary"
                          shape="square"
                          icon={<Trash />}
                          aria-label="삭제"
                          onClick={() => setDeleting(p)}
                        />
                      )}
                    </div>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Surface>

      {filtering ? (
        <TablePagination
          page={paged.page}
          setPage={paged.setPage}
          perPage={paged.perPage}
          total={paged.total}
        />
      ) : (
        data && (
          <TablePagination
            page={query.page}
            setPage={setPage}
            perPage={query.perPage}
            total={data.total}
          />
        )
      )}

      <Dialog.Root open={detail !== null} onOpenChange={(o) => !o && setDetail(null)}>
        <Dialog size="xl" className="max-h-[90vh] overflow-y-auto p-6">
          {detail && (
            <PostDetail
              post={detail}
              onClose={() => setDetail(null)}
              onComments={() => {
                setDetail(null)
                openComments(detail)
              }}
            />
          )}
        </Dialog>
      </Dialog.Root>

      <ConfirmDialog
        open={deleting !== null}
        title="이 글을 삭제할까요?"
        description={`"${deleting?.question ?? ''}" 이(가) 모든 사용자에게서 숨겨져요. 되돌릴 수 없어요.`}
        confirmLabel="삭제"
        destructive
        busy={busy}
        onConfirm={() => void remove()}
        onClose={() => setDeleting(null)}
      />
      <ConfirmDialog
        open={linkedCaseIds !== null}
        title={`연결된 완성 사례 ${linkedCaseIds?.length ?? 0}개도 숨길까요?`}
        description="글을 지워도 완성 사례는 갤러리에 남아요. 숨기면 갤러리에서 빠지고, 완성 사례 화면에서 다시 보이게 할 수 있어요."
        confirmLabel="사례도 숨기기"
        busy={busy}
        onConfirm={() => void hideLinkedCases()}
        onClose={() => setLinkedCaseIds(null)}
      />

      <Dialog.Root open={commentsOf !== null} onOpenChange={(o) => !o && setCommentsOf(null)}>
        <Dialog size="xl" className="max-h-[90vh] overflow-y-auto p-6">
          <Dialog.Title className="text-lg font-semibold">댓글</Dialog.Title>
          <Dialog.Description className="text-kumo-subtle mt-1">
            {commentsOf?.question ?? ''}
          </Dialog.Description>
          {commentsError && (
            <div className="mt-4">
              <Banner variant="error" title="문제가 생겼어요" description={commentsError} />
            </div>
          )}
          <div className="mt-4 flex flex-col gap-3">
            {comments === null ? (
              <div className="flex items-center gap-3">
                <Loader />
                <Text variant="secondary">불러오는 중…</Text>
              </div>
            ) : comments.length === 0 ? (
              <Text variant="secondary">댓글이 없어요.</Text>
            ) : (
              comments.map((cm) => (
                <div key={cm.id} className="border-kumo-line rounded-lg border p-3">
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="font-medium">{cm.authorName}</span>
                    <span className="text-kumo-subtle">{fmtTime(cm.createdAt)}</span>
                    {cm.reportCount > 0 && <Badge variant="warning">신고 {cm.reportCount}건</Badge>}
                    {cm.authorRestricted && <Badge variant="error">이용 제한</Badge>}
                    {cm.isDeleted && <Badge variant="error">삭제됨</Badge>}
                    <div className="ml-auto flex gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        icon={<Prohibit />}
                        onClick={() =>
                          setRestricting({
                            userId: cm.userId,
                            name: cm.authorName,
                            restrict: !cm.authorRestricted,
                          })
                        }
                      >
                        {cm.authorRestricted ? '제한 해제' : '작성자 제한'}
                      </Button>
                      {!cm.isDeleted && (
                        <Button
                          variant="secondary"
                          size="sm"
                          icon={<Trash />}
                          onClick={() => setDeletingComment(cm)}
                        >
                          삭제
                        </Button>
                      )}
                    </div>
                  </div>
                  <p className="mt-2 whitespace-pre-wrap break-words text-sm">{cm.body}</p>
                  <div className="text-kumo-subtle mt-1 font-mono text-xs">
                    {cm.id} · {cm.userId}
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="mt-6 flex justify-end">
            <Button variant="secondary" onClick={() => setCommentsOf(null)}>
              닫기
            </Button>
          </div>
        </Dialog>
      </Dialog.Root>

      <ConfirmDialog
        open={deletingComment !== null}
        title="이 댓글을 삭제할까요?"
        description={`"${deletingComment?.body.slice(0, 80) ?? ''}" 이(가) 모든 사용자에게서 숨겨져요. 되돌릴 수 없어요.`}
        confirmLabel="삭제"
        destructive
        busy={busy}
        onConfirm={() => void removeComment()}
        onClose={() => setDeletingComment(null)}
      />
      <ConfirmDialog
        open={restricting !== null}
        title={
          restricting?.restrict
            ? `${restricting.name} 님의 커뮤니티 이용을 제한할까요?`
            : `${restricting?.name ?? ''} 님의 이용 제한을 풀까요?`
        }
        description={
          restricting?.restrict
            ? '이 계정은 커뮤니티에 글·댓글을 쓸 수 없게 돼요. 이미 올린 글·댓글은 따로 삭제해야 해요. 나중에 해제할 수 있어요.'
            : '이 계정이 다시 커뮤니티에 글·댓글을 쓸 수 있어요.'
        }
        confirmLabel={restricting?.restrict ? '이용 제한' : '제한 해제'}
        destructive={restricting?.restrict ?? false}
        busy={busy}
        onConfirm={() => void applyRestrict()}
        onClose={() => setRestricting(null)}
      />
    </div>
  )
}

/** 목록의 고민 유형 칸. 메뉴 꼬리표는 개수만, 마우스를 올리면 전부 */
function TagSummary({ post }: { post: AdminPost }) {
  const concerns = concernLabels(post.concernTags)
  const rows = menuTagRows(post.menuTags, 'menu')
  const tagCount = rows.reduce((n, r) => n + r.labels.length, 0)
  if (!concerns.length && !tagCount) return <span className="text-kumo-subtle">없음</span>
  return (
    <div className="flex flex-wrap items-center gap-1">
      {concerns.map((c) => (
        <Badge key={c.id} variant="outline">
          {c.label}
        </Badge>
      ))}
      {tagCount > 0 && (
        <span
          className="text-kumo-subtle text-xs"
          title={rows.map((r) => `${r.axisLabel}: ${r.labels.join(', ')}`).join('\n')}
        >
          꼬리표 {tagCount}개
        </span>
      )}
    </div>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <Text variant="secondary" size="sm">
        {label}
      </Text>
      <div className="mt-1">{children}</div>
    </div>
  )
}

/** 글 상세: 출처, 고민 유형, 메뉴 꼬리표, 본문, 투표, 붙인 제품과 원가·마진 공개 여부 */
function PostDetail({
  post,
  onClose,
  onComments,
}: {
  post: AdminPost
  onClose: () => void
  onComments: () => void
}) {
  const navigate = useNavigate()
  const example = isExamplePost(post)
  const concerns = concernLabels(post.concernTags)
  const product = post.product
  const share = costShareOf(product)
  const picked = product?.majorId
    ? taxonomyLabel(product.axis, product.majorId, product.subId, 'ko')
    : null
  return (
    <div data-testid="post-detail">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <Dialog.Title className="text-lg font-semibold">{post.question}</Dialog.Title>
          <Dialog.Description className="text-kumo-subtle">
            {post.authorName} · {fmtTime(post.createdAt)} · {post.language} ·{' '}
            {post.isDeleted ? '삭제됨' : '게시 중'}
          </Dialog.Description>
        </div>
        <Button
          variant="secondary"
          shape="square"
          icon={<X />}
          aria-label="닫기"
          onClick={onClose}
        />
      </div>

      <div className="flex flex-col gap-4 text-sm">
        <Field label="글 출처">
          {example ? (
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="info">예시 고민</Badge>
              <span className="text-kumo-subtle">
                운영팀이 정리한 예시 글이에요. 앱에도 예시 표시가 붙어요.
              </span>
            </div>
          ) : (
            '사용자 글'
          )}
        </Field>
        <Field label="고민 유형">
          {concerns.length ? (
            <div className="flex flex-wrap gap-1">
              {concerns.map((c) => (
                <Badge key={c.id} variant="outline">
                  {c.label}
                </Badge>
              ))}
            </div>
          ) : (
            <span className="text-kumo-subtle">없음</span>
          )}
        </Field>
        <Field label="메뉴 꼬리표">
          <MenuTagList rows={menuTagRows(post.menuTags, 'menu')} />
        </Field>
        <Field label="본문">
          {post.body ? (
            <div className="whitespace-pre-wrap break-words">{post.body}</div>
          ) : (
            <span className="text-kumo-subtle">없음</span>
          )}
        </Field>
        {post.options.length > 0 && (
          <Field label={post.pollTitle ? `투표: ${post.pollTitle}` : '투표'}>
            <div>
              {post.options.join(' / ')}{' '}
              <span className="text-kumo-subtle">({fmtInt(post.totalVotes)}표)</span>
            </div>
          </Field>
        )}
        <Field label="붙인 제품">
          {product ? (
            <div className="flex flex-col gap-1">
              <div>
                {product.name}
                {product.tagline ? (
                  <span className="text-kumo-subtle"> · {product.tagline}</span>
                ) : null}
              </div>
              <div className="text-kumo-subtle">
                {picked
                  ? `${picked.major}${picked.sub ? ` · ${picked.sub}` : ''}`
                  : productCategoryLabel(product.categoryId)}{' '}
                · 판매가 {fmtMoney(product.suggestedRetail, product.currency)}
              </div>
              <div className="flex flex-wrap items-center gap-2" data-testid="cost-share">
                <span>원가·마진</span>
                {share === 'shared' ? (
                  <>
                    <Badge variant="warning">공개</Badge>
                    <span>
                      {product.unitCost != null
                        ? `원가 ${fmtMoney(product.unitCost, product.currency)}`
                        : null}
                      {product.unitCost != null && product.marginPct != null ? ' · ' : null}
                      {product.marginPct != null
                        ? `마진 ${Math.round(product.marginPct * 100)}%`
                        : null}
                    </span>
                    <span className="text-kumo-subtle">
                      {example
                        ? '앱 카드에 예시 수치 표시와 함께 보여요.'
                        : '앱 카드에 모두에게 보여요.'}
                    </span>
                  </>
                ) : (
                  <>
                    <Badge variant="secondary">비공개</Badge>
                    <span className="text-kumo-subtle">앱 카드에는 판매가만 보여요.</span>
                  </>
                )}
              </div>
              {post.simulationId && (
                <div>
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={<Flask />}
                    onClick={() =>
                      navigate(`/simulations?open=${encodeURIComponent(post.simulationId ?? '')}`)
                    }
                  >
                    시뮬레이션 보기
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <span className="text-kumo-subtle">없음</span>
          )}
        </Field>
        <Field label="사진">
          {post.images.length ? (
            `${post.images.length}장`
          ) : (
            <span className="text-kumo-subtle">없음</span>
          )}
        </Field>
        <div className="text-kumo-subtle font-mono text-xs">
          {post.id} · {post.userId}
          {post.simulationId ? ` · ${post.simulationId}` : ''}
        </div>
      </div>

      <div className="mt-6 flex justify-end gap-2">
        <Button variant="secondary" icon={<ChatCircle />} onClick={onComments}>
          댓글 {fmtInt(post.commentCount)}개 보기
        </Button>
        <Button variant="secondary" onClick={onClose}>
          닫기
        </Button>
      </div>
    </div>
  )
}
